import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageSquare, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSending(true);
    
    // Simulate sending (in production, this would call a backend API)
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setSending(false);
    setSent(true);
    toast.success('Message sent! We\'ll get back to you soon.');
    
    // Reset form after a delay
    setTimeout(() => {
      setFormData({ name: '', email: '', subject: '', message: '' });
      setSent(false);
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-[#0a0c10] pt-16">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#4ADE80]/10 to-transparent py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-['Barlow_Condensed'] uppercase tracking-wide">
            Contact <span className="text-[#4ADE80]">Us</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Have questions? We're here to help. Reach out to our team.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="space-y-4">
            {/* Email */}
            <div className="bg-[#12151a] rounded-xl border border-[#1a1d24] p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#4ADE80]/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#4ADE80]" />
                </div>
                <h3 className="font-semibold text-white">Email</h3>
              </div>
              <a 
                href="mailto:support@haullogixloads.com" 
                className="text-[#4ADE80] hover:underline"
              >
                support@haullogixloads.com
              </a>
              <p className="text-gray-500 text-sm mt-1">We respond within 24 hours</p>
            </div>

            {/* Phone */}
            <div className="bg-[#12151a] rounded-xl border border-[#1a1d24] p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#4ADE80]/20 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-[#4ADE80]" />
                </div>
                <h3 className="font-semibold text-white">Phone</h3>
              </div>
              <a 
                href="tel:+13133142922" 
                className="text-[#4ADE80] hover:underline"
              >
                (313) 314-2922
              </a>
              <p className="text-gray-500 text-sm mt-1">Mon-Fri 8am-6pm EST</p>
            </div>

            {/* Location */}
            <div className="bg-[#12151a] rounded-xl border border-[#1a1d24] p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#4ADE80]/20 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#4ADE80]" />
                </div>
                <h3 className="font-semibold text-white">Location</h3>
              </div>
              <p className="text-gray-300">Detroit, Michigan</p>
              <p className="text-gray-500 text-sm mt-1">United States</p>
            </div>

            {/* Response Time */}
            <div className="bg-[#12151a] rounded-xl border border-[#1a1d24] p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-green-400" />
                </div>
                <h3 className="font-semibold text-white">Response Time</h3>
              </div>
              <p className="text-gray-300">Typically within 24 hours</p>
              <p className="text-gray-500 text-sm mt-1">Urgent issues prioritized</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-[#12151a] rounded-xl border border-[#1a1d24] p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#4ADE80]/20 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-[#4ADE80]" />
                </div>
                <h2 className="text-xl font-bold text-white">Send us a Message</h2>
              </div>

              {sent ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">Message Sent!</h3>
                  <p className="text-gray-400">We'll get back to you as soon as possible.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-gray-300">Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="Your name"
                        className="mt-1 bg-[#0a0c10] border-[#1a1d24]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-gray-300">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="your@email.com"
                        className="mt-1 bg-[#0a0c10] border-[#1a1d24]"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-gray-300">Subject</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      placeholder="What's this about?"
                      className="mt-1 bg-[#0a0c10] border-[#1a1d24]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-300">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="How can we help you?"
                      className="mt-1 bg-[#0a0c10] border-[#1a1d24] min-h-[150px]"
                      required
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={sending}
                    className="w-full bg-[#4ADE80] hover:bg-[#22C55E] text-black font-semibold py-3"
                  >
                    {sending ? (
                      <>Sending...</>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* FAQ Link */}
            <div className="mt-6 text-center text-gray-400">
              <p>
                Looking for quick answers? Check our{' '}
                <a href="/backlot" className="text-[#4ADE80] hover:underline">
                  Community Forum
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
